import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import { formatarMoeda, formatarCnpjCpf } from "../../../../utils/solicitacoes/formatters";
import SolicitacaoCompraService from "../../../../services/resource/solicitacaoCompraService";
import TipoPagamentoService from "../../../../services/resource/tipoPagamentoService";
import SelecionarFornecedorModal from "../shared/SelecionarFornecedorModal";
import InputMoney from "../../../../componentes/input/inputMoney";
import DateMasked from "../../../../componentes/inputMask/DateMasked";

const service = new SolicitacaoCompraService();
const tipoPagamentoService = new TipoPagamentoService();

const TIPO_DESPESA_OPTIONS = [
    { value: "SUPRIMENTOS", label: "Suprimentos" },
    { value: "CONTAS_A_PAGAR", label: "Contas a Pagar" },
];

const DESCRICAO_NOTA_FISCAL = "Nota Fiscal";

export default function EtapaOrdemCompra({ solicitacao, detalheProcesso, onAcaoConcluida, encaminhar, anexoObrigatorioNaEtapa, temAnexo, anexar, anexos = [] }) {
    const [confirmacaoCompraRealizada, setConfirmacaoCompraRealizada] = useState(detalheProcesso?.confirmacaoCompraRealizada ?? true);
    const [valorCompra, setValorCompra] = useState(detalheProcesso?.valorCompra ?? detalheProcesso?.valorFinalCotacao ?? "");
    const [vencimento, setVencimento] = useState(detalheProcesso?.vencimento ?? detalheProcesso?.vencimento ?? "");
    const [fornecedorId, setFornecedorId] = useState(detalheProcesso?.fornecedorId ?? null);
    const [fornecedorSelecionado, setFornecedorSelecionado] = useState(
        detalheProcesso?.fornecedorId ? { id: detalheProcesso.fornecedorId, nome: detalheProcesso.fornecedorNome } : null
    );
    const [modalFornecedorAberto, setModalFornecedorAberto] = useState(false);
    const [tipoDespesa, setTipoDespesa] = useState(detalheProcesso?.tipoDespesa ?? "SUPRIMENTOS");
    const [descricaoDespesa, setDescricaoDespesa] = useState(detalheProcesso?.descricaoDespesa || "");
    const [tipoPagamento, setTipoPagamento] = useState(detalheProcesso?.tipoPagamento || "");
    const [tipoConta, setTipoConta] = useState(detalheProcesso?.tipoConta || "");
    const [agencia, setAgencia] = useState(detalheProcesso?.agencia || "");
    const [agenciaDigito, setAgenciaDigito] = useState(detalheProcesso?.agenciaDigito || "");
    const [conta, setConta] = useState(detalheProcesso?.conta || "");
    const [contaDigito, setContaDigito] = useState(detalheProcesso?.contaDigito || "");
    const [chavePix, setChavePix] = useState(detalheProcesso?.chavePix || "");
    const [tiposPagamento, setTiposPagamento] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [enviandoNotaFiscal, setEnviandoNotaFiscal] = useState(false);

    useEffect(() => {
        tipoPagamentoService.listar().then(res => setTiposPagamento(res.data)).catch(() => setTiposPagamento([]));
    }, []);

    function selecionarFornecedor(fornecedor) {
        setFornecedorSelecionado(fornecedor);
        setFornecedorId(fornecedor.id);
    }

    // Anexo real (já enviado pro backend) mais recente com essa descrição — mesmo padrão usado
    // pra mostrar o anexo por fornecedor na Cotação (ver CotacaoCamposEditor#anexoDoFornecedor).
    const anexosNotaFiscal = anexos.filter(a => a.descricao === DESCRICAO_NOTA_FISCAL);
    const anexoNotaFiscal = anexosNotaFiscal[anexosNotaFiscal.length - 1] || null;

    function anexarNotaFiscal(evento) {
        const arquivo = evento.target.files?.[0];
        evento.target.value = "";
        if (!arquivo || !anexar) return;

        setEnviandoNotaFiscal(true);
        anexar(arquivo, DESCRICAO_NOTA_FISCAL)
            .then(() => toast.success("Nota fiscal anexada!"))
            .catch(() => toast.error("Erro ao anexar a nota fiscal."))
            .finally(() => setEnviandoNotaFiscal(false));
    }

    async function enviar() {
        if (confirmacaoCompraRealizada === null || !valorCompra || !fornecedorId || !tipoDespesa
            || !descricaoDespesa.trim() || !tipoPagamento.trim()) {
            toast.error("Preencha todos os campos obrigatórios.");
            return;
        }
        if (anexoObrigatorioNaEtapa && !temAnexo) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return;
        }

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("CONCLUIR");
        } catch {
            return;
        }

        setEnviando(true);
        service.preencherOrdemCompra(solicitacao.id, {
            confirmacaoCompraRealizada,
            valorCompra: Number(valorCompra),
            fornecedorId,
            tipoDespesa,
            descricaoDespesa,
            tipoPagamento,
            tipoConta,
            agencia,
            agenciaDigito,
            conta,
            contaDigito,
            chavePix,
            vencimento,
            usuarioEscolhidoId,
        })
            .then(res => onAcaoConcluida("REGISTRAR", res.data))
            .catch(error => console.error("Erro ao registrar ordem de compra", error))
            .finally(() => setEnviando(false));
    }

    return (
        <div className="flex flex-col gap-4">
            {detalheProcesso?.valorFinalCotacao != null && (
                <p className="text-xs text-muted-foreground">
                    Valor final negociado na cotação: <span className="font-medium text-foreground">{formatarMoeda(detalheProcesso.valorFinalCotacao)}</span>
                </p>
            )}

            <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-foreground/80">Confirmação de compra realizada?</span>
                <div className="flex gap-2">
                    <Button variant={confirmacaoCompraRealizada === true ? "green" : "outline"} label="Sim" onClick={() => setConfirmacaoCompraRealizada(true)} />
                    <Button variant={confirmacaoCompraRealizada === false ? "red" : "outline"} label="Não" onClick={() => setConfirmacaoCompraRealizada(false)} />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Fornecedor <span className="text-destructive">*</span></label>
                    <Button
                        variant="outline"
                        label={fornecedorSelecionado ? "Trocar Fornecedor" : "Buscar Fornecedor"}
                        iconLeft="fa fa-search"
                        onClick={() => setModalFornecedorAberto(true)}
                    />
                    {fornecedorSelecionado && (
                        <span className="text-xs text-muted-foreground">
                            {fornecedorSelecionado.nome}{fornecedorSelecionado.cnpj || fornecedorSelecionado.cpf ? ` — ${formatarCnpjCpf(fornecedorSelecionado.cnpj || fornecedorSelecionado.cpf)}` : ""}
                        </span>
                    )}
                </div>
                <InputMoney
                    type="number"
                    label="Valor da compra"
                    required value={valorCompra}
                    onFocus={(e) => e.target.select()}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    mode="decimal"
                    onChange={e => setValorCompra(e.target.value)}
                />
                <DateMasked type="date" label="Vencimento" required value={vencimento} onChange={e => setVencimento(e)} />
            </div>

            {anexar && (
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Nota Fiscal</label>
                    <div className="flex items-start gap-2 flex-wrap">
                        <label className="flex items-center justify-center gap-1.5 h-9 px-3 w-full sm:w-64 text-xs border border-dashed border-border rounded-lg cursor-pointer text-muted-foreground hover:border-primary/50 hover:bg-muted">
                            <i className={`fa ${enviandoNotaFiscal ? "fa-spinner fa-spin" : "fa-paperclip"}`} />
                            {enviandoNotaFiscal ? "Enviando..." : anexoNotaFiscal ? "Anexar outra nota fiscal" : "Anexar nota fiscal"}
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                disabled={enviandoNotaFiscal}
                                onChange={anexarNotaFiscal}
                            />
                        </label>
                        {anexoNotaFiscal && (
                            <div
                                title={anexoNotaFiscal.nomeOriginal}
                                className="flex items-center gap-1.5 h-9 px-3 max-w-full sm:max-w-64 text-xs bg-success/10 text-success rounded-lg"
                            >
                                <i className="fa fa-paperclip shrink-0" />
                                <span className="truncate">{anexoNotaFiscal.nomeOriginal}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <SelecionarFornecedorModal
                isOpen={modalFornecedorAberto}
                onClose={() => setModalFornecedorAberto(false)}
                onSelecionar={selecionarFornecedor}
            />

            <Input label="Descrição da despesa" required value={descricaoDespesa} onChange={e => setDescricaoDespesa(e.target.value)} />

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Tipo da despesa <span className="text-destructive">*</span></label>
                    <CustomDropdown value={tipoDespesa} onChange={e => setTipoDespesa(e.value)} options={TIPO_DESPESA_OPTIONS} placeholder="Selecione" />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Tipo de pagamento <span className="text-destructive">*</span></label>
                    <CustomDropdown value={tipoPagamento} onChange={e => setTipoPagamento(e.value)} options={tiposPagamento} optionValue="nome" optionLabel="nome" placeholder="Selecione" filter />
                </div>

                {tipoPagamento === "PIX" && (
                    <div className="col-span-2">
                        <Input label="Chave PIX" required value={chavePix} onChange={e => setChavePix(e.target.value)} />
                    </div>
                )}

            </div>

            {tipoPagamento === "Transferência" && (
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                    <div className="col-span-2 flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Tipo de Conta <span className="text-destructive">*</span></label>
                        <CustomDropdown value={tipoConta} onChange={e => setTipoConta(e.value)} options={[{ nome: "Conta Corrente" }, { nome: "Conta Poupança" }]} optionValue="nome" optionLabel="nome" placeholder="Selecione" filter />
                    </div>
                    <Input type="number" label="Agencia" required value={agencia} onChange={e => setAgencia(e.target.value)} />
                    <Input type="number" label="Digito" required value={agenciaDigito} onChange={e => setAgenciaDigito(e.target.value)} />
                    <Input type="number" label="Conta" required value={conta} onChange={e => setConta(e.target.value)} />
                    <Input type="number" label="Digito" required value={contaDigito} onChange={e => setContaDigito(e.target.value)} />
                </div>
            )}

            <div className="flex justify-end">
                <Button variant="blue" label="Registrar ordem de compra" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </div>
    );
}
